import { useTranslation } from 'react-i18next';

import type { CardPhase } from '../../../api/cards';
import type { CardProgressData, CardResult } from '../../../types';
import { Spinner } from '../Spinner';
import { BatchProgress } from './BatchProgress';
import { ResultsTable } from './ResultsTable';
import type { ThrottleState } from './use-card-batch';

/**
 * The `uploading` and `committing` phases: a request is in flight and there is
 * nothing to show yet but which one it is.
 */
export function BusyNotice({ phase }: { phase: CardPhase }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 text-sm text-muted">
      <Spinner />
      {phase === 'committing'
        ? t('cardGenerator.committing')
        : t('cardGenerator.uploading')}
    </div>
  );
}

/**
 * The two streaming phases, `preflighting` and `processing`. Same shape for
 * both — a progress bar and the rows that have arrived — since both publish to
 * the same channel; only the wording differs.
 */
export function RunningStep({
  phase,
  progress,
  throttled,
  finalizing,
  results,
  jobId,
  onCancel,
}: {
  phase: CardPhase;
  progress: CardProgressData;
  throttled: ThrottleState | null;
  finalizing: boolean;
  results: CardResult[];
  jobId: string | null;
  onCancel: () => void;
}) {
  return (
    <div className="space-y-4">
      <BatchProgress
        phase={phase}
        progress={progress}
        throttled={throttled}
        finalizing={finalizing}
        onCancel={onCancel}
      />
      {results.length > 0 && (
        <ResultsTable results={results} jobId={jobId} phase={phase} />
      )}
    </div>
  );
}
